'use client'

import type { ClientPriceProps } from '@fukku/price/types'
import { featureFlagKeys } from 'feature-flags/constants/featureFlags'
import { useFeatureFlag } from 'feature-flags/hooks/useFeatureFlag/useFeatureFlag'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { PricesDispatcher } from '../components/PricesDispatcher/PricesDispatcher'
import { Price } from '../components/price/Price'
import { type OmnibusPriceFF, PricesProvider } from '../context/PricesProvider'
import { getHideSaleOrPromoPrice } from '../utils/getHideSaleOrPromoPrice/getHideSaleOrPromoPrice'
import { getPricesStoreData } from '../utils/getPricesStoreData/getPricesStoreData'

/**
 * @description
 * Renders a generic price with the possibility of adding crossed out prices. It is a client component
 *
 * @example
 * <ClientPrice prices={{price: 10}} />
 *
 * @example
 * <ClientPrice
 * prices={{
 * 		price: 10,
 * 		crossedOutPrices: [40, 30, 20],
 * 		hideLowestPrice: [40, 30]
 * 		discountRate: 50,
 * }}
 * horizontal
 * reverse
 * align="start"
 * additionalPriceVertical
 * hasMicrodata
 * hidePriceHistory
 * showDiscount
 * showDiscountInfo
 * classes={{
 * 		price: text.bodyL,
 * 		crossedOutPrices: '',
 * 		firstCrossedOutPrice: '',
 * 		discountRate: text.bodyS,
 * 		additionalPrice: '',
 * 		additionalFirstCrossedOutPrice: '',
 * 		additionalCrossedOutPrices: '',
 * }}
 * />
 *
 * @see {@link ServerPrice}
 */
export const ClientPrice = ({
	prices,
	showAdditionalCurrencies,
	showOmnibusPrice,
	dataTestId,
	...props
}: ClientPriceProps) => {
	const { country, channel } = useMasterData()

	const pricesStoreData = getPricesStoreData({
		prices,
		showAdditionalCurrencies,
		country,
	})

	const hideSaleOrPromoPrice = getHideSaleOrPromoPrice(
		useFeatureFlag<OmnibusPriceFF>(featureFlagKeys.OmnibusExceptionCountries),
		country.countryISO,
		showOmnibusPrice ?? true
	)

	return (
		<PricesProvider
			showAdditionalCurrencies={showAdditionalCurrencies}
			discountRate={prices.discountRate}
			hideSaleOrPromoPrice={hideSaleOrPromoPrice}
			country={country}
			channel={channel}
			{...pricesStoreData}
			{...props}
		>
			<PricesDispatcher prices={prices} />
			<Price dataTestId={dataTestId} />
		</PricesProvider>
	)
}
